import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { PayRates } from "../domain/PayRates";
import { ReportsAPI } from "../infrastructure/reports.api";
import { PRACTITIONER_REPORT_KEY } from "./usePractitionerReport";

export const PAY_RATES_KEY = "pay-rates";

/** The caller's own rates, and their centre's as they apply to them (read-only). */
export function usePayRates() {
    const queryClient = useQueryClient();
    const query = useQuery({
        queryKey: [PAY_RATES_KEY, "self"],
        queryFn: async () => (await ReportsAPI.myRates()).data,
        retry: false,
    });
    const save = useMutation({
        mutationFn: (rates: PayRates) => ReportsAPI.setMyRates(rates),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [PAY_RATES_KEY] });
            // Amounts are priced at read time: every report open is now stale.
            queryClient.invalidateQueries({ queryKey: [PRACTITIONER_REPORT_KEY] });
        },
    });
    return {
        rates: query.data,
        loading: query.isLoading,
        error: query.isError,
        save: save.mutateAsync,
        saving: save.isPending,
    };
}

/** A centre's rates and its members' overrides, for its administrator. */
export function useCenterPayRates(centerId: string | undefined) {
    const queryClient = useQueryClient();
    const query = useQuery({
        queryKey: [PAY_RATES_KEY, "center", centerId],
        queryFn: async () => (await ReportsAPI.centreRates(centerId as string)).data,
        enabled: Boolean(centerId),
        retry: false,
    });
    const invalidate = () => {
        queryClient.invalidateQueries({ queryKey: [PAY_RATES_KEY] });
        queryClient.invalidateQueries({ queryKey: [PRACTITIONER_REPORT_KEY] });
    };
    const saveCentre = useMutation({
        mutationFn: (rates: PayRates) => ReportsAPI.setCentreRates(centerId as string, rates),
        onSuccess: invalidate,
    });
    const saveMember = useMutation({
        mutationFn: ({ therapistId, rates }: { therapistId: string; rates: PayRates }) =>
            ReportsAPI.setMemberRates(centerId as string, therapistId, rates),
        onSuccess: invalidate,
    });
    return {
        rates: query.data,
        loading: query.isLoading,
        error: query.isError,
        retry: () => void query.refetch(),
        saveCentre: saveCentre.mutateAsync,
        saveMember: saveMember.mutateAsync,
        saving: saveCentre.isPending || saveMember.isPending,
    };
}
